import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';
import { toast } from 'react-toastify';
import './Manufacturing.css';

const Manufacturing = ({ activeTab: propActiveTab }) => {
  const [activeTab, setActiveTab] = useState('recipe');

  useEffect(() => {
    if (propActiveTab) {
      if (['recipe', 'production', 'settings', 'report'].includes(propActiveTab)) {
        setActiveTab(propActiveTab);
      }
    }
  }, [propActiveTab]);

  // Recipe state
  const [recipes, setRecipes] = useState([]);
  const [recipeLoading, setRecipeLoading] = useState(false);
  const [selectedRecipes, setSelectedRecipes] = useState([]);
  const [recipeSearch, setRecipeSearch] = useState('');
  const [recipeEntries, setRecipeEntries] = useState(25);

  // Production state
  const [productions, setProductions] = useState([]);
  const [productionLoading, setProductionLoading] = useState(false);
  const [prodFilterLocation, setProdFilterLocation] = useState('');
  const [prodFilterStartDate, setProdFilterStartDate] = useState('');
  const [prodFilterEndDate, setProdFilterEndDate] = useState('');
  const [prodFilterIsFinal, setProdFilterIsFinal] = useState(false);
  const [prodPage, setProdPage] = useState(1);
  const [prodLastPage, setProdLastPage] = useState(1);
  const [prodSearch, setProdSearch] = useState('');

  // Settings state
  const [mfgSettings, setMfgSettings] = useState({});
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [refNoPrefix, setRefNoPrefix] = useState('');
  const [disableEditQty, setDisableEditQty] = useState(false);
  const [enableUpdatePrice, setEnableUpdatePrice] = useState(false);

  // Report state
  const [reportData, setReportData] = useState({ total_production: 0, total_production_cost: 0, total_sold: 0 });
  const [reportLoading, setReportLoading] = useState(false);
  const [reportStartDate, setReportStartDate] = useState('');
  const [reportEndDate, setReportEndDate] = useState('');
  const [reportLocationId, setReportLocationId] = useState('');
  const [reportDateFilter, setReportDateFilter] = useState('');
  const [showDateFilterDropdown, setShowDateFilterDropdown] = useState(false);
  const [locations, setLocations] = useState([]);
  const [products, setProducts] = useState([]);

  // Modals
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [showProductionModal, setShowProductionModal] = useState(false);
  const [viewRecipeModal, setViewRecipeModal] = useState(null);
  const [viewProductionModal, setViewProductionModal] = useState(null);
  const [showAddRecipeFormModal, setShowAddRecipeFormModal] = useState(false);

  // Recipe add form
  const [recipeProductId, setRecipeProductId] = useState('');
  const [copyRecipeId, setCopyRecipeId] = useState('');
  const [recipeForm, setRecipeForm] = useState({
    variationId: '',
    totalQuantity: 1,
    wastePercent: 0,
    extraCost: 0,
    productionCostType: 'fixed',
    instructions: '',
    ingredients: [{ ingredient_id: '', quantity: 1, waste_percent: 0 }]
  });

  // Production add form
  const [prodForm, setProdForm] = useState({
    refNo: '',
    manufacturingDate: new Date().toISOString().slice(0, 16),
    locationId: '',
    productId: '',
    quantity: 1,
    wastedQty: 0,
    productionCost: 0,
    productionCostType: 'fixed',
    finalize: false,
  });

  useEffect(() => {
    fetchLocations();
    fetchProducts();
  }, []);

  useEffect(() => {
    if (activeTab === 'recipe') fetchRecipes();
    if (activeTab === 'production') fetchProductions();
    if (activeTab === 'settings') fetchSettings();
    if (activeTab === 'report') fetchReport();
  }, [activeTab]);

  const fetchLocations = async () => {
    try {
      const resp = await apiService.getLocations();
      setLocations(resp?.data || resp || []);
    } catch (e) { /* console.warn('Failed to fetch locations:', e); */ }
  };

  const fetchProducts = async () => {
    try {
      const resp = await apiService.getProducts({ only_mfg_products: 1 });
      setProducts(resp?.data || resp || []);
    } catch (e) { /* console.warn('Failed to fetch products:', e); */ }
  };

  const fetchRecipes = async () => {
    setRecipeLoading(true);
    try {
      const resp = await apiService.getManufacturingRecipes();
      setRecipes(resp?.data || []);
    } catch (e) {
      /* console.error('Error fetching recipes:', e); */
    } finally {
      setRecipeLoading(false);
    }
  };

  const fetchProductions = async (page = 1) => {
    setProductionLoading(true);
    try {
      const params = { page };
      if (prodFilterLocation) params.location_id = prodFilterLocation;
      if (prodFilterStartDate && prodFilterEndDate) {
        params.start_date = prodFilterStartDate;
        params.end_date = prodFilterEndDate;
      }
      if (prodFilterIsFinal) params.is_final = 1;
      const resp = await apiService.getManufacturingProductions(params);
      setProductions(resp?.data || []);
      setProdPage(resp?.pagination?.current_page || 1);
      setProdLastPage(resp?.pagination?.last_page || 1);
    } catch (e) {
      /* console.error('Error fetching productions:', e); */
    } finally {
      setProductionLoading(false);
    }
  };

  const fetchSettings = async () => {
    setSettingsLoading(true);
    try {
      const resp = await apiService.getManufacturingSettings();
      const s = resp?.settings || {};
      setMfgSettings(s);
      setRefNoPrefix(s.ref_no_prefix || '');
      setDisableEditQty(!!s.disable_editing_ingredient_qty);
      setEnableUpdatePrice(!!s.enable_updating_product_price);
    } catch (e) {
      /* console.error('Error fetching settings:', e); */
    } finally {
      setSettingsLoading(false);
    }
  };

  const fetchReport = async () => {
    setReportLoading(true);
    try {
      const params = {};
      if (reportStartDate && reportEndDate) {
        params.start_date = reportStartDate;
        params.end_date = reportEndDate;
      }
      if (reportLocationId) params.location_id = reportLocationId;
      const resp = await apiService.getManufacturingReport(params);
      setReportData(resp || { total_production: 0, total_production_cost: 0, total_sold: 0, production_count: 0 });
    } catch (e) {
      /* console.error('Error fetching report:', e); */
    } finally {
      setReportLoading(false);
    }
  };

  const handleViewRecipe = async (id) => {
    try {
      const res = await apiService.getManufacturingRecipeDetails(id);
      if (res && res.recipe) {
        setViewRecipeModal(res);
      } else {
        const found = recipes.find(r => r.id === id);
        setViewRecipeModal({ recipe: found, ingredients: [] });
      }
    } catch (e) {
      const found = recipes.find(r => r.id === id);
      setViewRecipeModal({ recipe: found, ingredients: [] });
    }
  };

  const handleViewProduction = async (id) => {
    try {
      const res = await apiService.getManufacturingProductionDetails(id);
      if (res && res.production_purchase) {
        setViewProductionModal(res);
      } else {
        const found = productions.find(p => p.id === id);
        setViewProductionModal({ production_purchase: found, ingredients: [] });
      }
    } catch (e) {
      const found = productions.find(p => p.id === id);
      setViewProductionModal({ production_purchase: found, ingredients: [] });
    }
  };

  const handleEditRecipe = async (id) => {
    try {
      const res = await apiService.getManufacturingRecipeDetails(id);
      if (res && res.recipe) {
        const r = res.recipe;
        setRecipeForm({
          variationId: r.variation_id || '',
          totalQuantity: parseFloat(r.total_quantity) || 1,
          wastePercent: parseFloat(r.waste_percent) || 0,
          extraCost: parseFloat(r.extra_cost) || 0,
          productionCostType: r.production_cost_type || 'fixed',
          instructions: r.instructions || '',
          ingredients: (res.ingredients && res.ingredients.length > 0) ? res.ingredients.map(ing => ({
            ingredient_id: ing.variation_id || ing.ingredient_id || '',
            quantity: parseFloat(ing.quantity) || 1,
            waste_percent: parseFloat(ing.waste_percent) || 0
          })) : [{ ingredient_id: '', quantity: 1, waste_percent: 0 }]
        });
        setShowAddRecipeFormModal(true);
      } else {
        toast.error('Recipe details not found');
      }
    } catch (e) {
      toast.error('Error fetching recipe details');
    }
  };

  const handleDeleteRecipe = async (id) => {
    if (!window.confirm('Are you sure you want to delete this recipe?')) return;
    try {
      await apiService.deleteManufacturingRecipe(id);
      fetchRecipes();
    } catch (e) {
      toast.error('Failed to delete recipe');
    }
  };

  const handleDeleteProduction = async (id) => {
    if (!window.confirm('Are you sure you want to delete this production?')) return;
    try {
      await apiService.deleteManufacturingProduction(id);
      fetchProductions(prodPage);
    } catch (e) {
      toast.error('Failed to delete production');
    }
  };

  const handleSaveSettings = async () => {
    try {
      await apiService.saveManufacturingSettings({
        ref_no_prefix: refNoPrefix,
        disable_editing_ingredient_qty: disableEditQty ? 1 : 0,
        enable_updating_product_price: enableUpdatePrice ? 1 : 0,
      });
      toast.success('Settings saved successfully');
    } catch (e) {
      toast.error('Failed to save settings');
    }
  };

  const handleUpdateProductPrices = async () => {
    if (selectedRecipes.length === 0) {
      toast.warning('Please select at least one recipe');
      return;
    }
    const unitPrices = {};
    selectedRecipes.forEach(id => {
      const r = recipes.find(rec => rec.id === id);
      if (r) unitPrices[id] = r.unit_cost;
    });
    try {
      await apiService.updateManufacturingProductPrices({ recipe_ids: selectedRecipes, unit_prices: unitPrices });
      toast.success('Product prices updated successfully');
      setSelectedRecipes([]);
    } catch (e) {
      toast.error('Failed to update product prices');
    }
  };

  const toggleRecipeSelect = (id) => {
    setSelectedRecipes(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAllRecipes = (e) => {
    if (e.target.checked) {
      setSelectedRecipes(recipes.map(r => r.id));
    } else {
      setSelectedRecipes([]);
    }
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value) || 0;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
  };

  const filteredRecipes = recipes.filter(r =>
    !recipeSearch || r.recipe_name?.toLowerCase().includes(recipeSearch.toLowerCase())
  );

  const filteredProductions = productions.filter(p =>
    !prodSearch ||
    p.ref_no?.toLowerCase().includes(prodSearch.toLowerCase()) ||
    p.product_name?.toLowerCase().includes(prodSearch.toLowerCase()) ||
    p.location_name?.toLowerCase().includes(prodSearch.toLowerCase())
  );

  // Top KPI Overview Cards
  const totalProductionVal = productions.reduce((acc, cur) => acc + (parseFloat(cur.final_total) || 0), 0);

  const renderStatsHeader = () => (
    <div className="mfg-stats-grid">
      <div className="mfg-stat-card">
        <div className="mfg-stat-icon-wrap" style={{ background: 'rgba(79, 70, 229, 0.1)', color: '#4f46e5' }}>
          <i className="fas fa-scroll" />
        </div>
        <div className="mfg-stat-info">
          <div className="mfg-stat-label">Total Recipes</div>
          <div className="mfg-stat-value">{recipes.length}</div>
          <div className="mfg-stat-subtext">Configured recipes</div>
        </div>
      </div>

      <div className="mfg-stat-card">
        <div className="mfg-stat-icon-wrap" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
          <i className="fas fa-industry" />
        </div>
        <div className="mfg-stat-info">
          <div className="mfg-stat-label">Production Batches</div>
          <div className="mfg-stat-value">{productions.length}</div>
          <div className="mfg-stat-subtext">Completed & pending</div>
        </div>
      </div>

      <div className="mfg-stat-card">
        <div className="mfg-stat-icon-wrap" style={{ background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}>
          <i className="fas fa-sack-dollar" />
        </div>
        <div className="mfg-stat-info">
          <div className="mfg-stat-label">Production Value</div>
          <div className="mfg-stat-value">{formatCurrency(reportData.total_production || totalProductionVal)}</div>
          <div className="mfg-stat-subtext">Calculated total value</div>
        </div>
      </div>

      <div className="mfg-stat-card">
        <div className="mfg-stat-icon-wrap" style={{ background: enableUpdatePrice ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)', color: enableUpdatePrice ? '#10b981' : '#f59e0b' }}>
          <i className={enableUpdatePrice ? "fas fa-sync" : "fas fa-cog"} />
        </div>
        <div className="mfg-stat-info">
          <div className="mfg-stat-label">Auto Price Sync</div>
          <div className="mfg-stat-value" style={{ fontSize: '1.15rem' }}>
            <span className={`mfg-badge ${enableUpdatePrice ? 'mfg-badge-success' : 'mfg-badge-warning'}`}>
              {enableUpdatePrice ? 'Enabled' : 'Disabled'}
            </span>
          </div>
          <div className="mfg-stat-subtext">Product price update</div>
        </div>
      </div>
    </div>
  );

  const renderTabsNav = () => (
    <div className="mfg-tabs-nav">
      {[
        { key: 'recipe', label: 'Recipe Catalog', icon: 'fas fa-scroll' },
        { key: 'production', label: 'Production Runs', icon: 'fas fa-industry' },
        { key: 'settings', label: 'Module Settings', icon: 'fas fa-sliders' },
        { key: 'report', label: 'Analytics Report', icon: 'fas fa-chart-line' },
      ].map(tab => (
        <button
          key={tab.key}
          className={`mfg-tab-btn ${activeTab === tab.key ? 'active' : ''}`}
          onClick={() => setActiveTab(tab.key)}
        >
          <i className={tab.icon} />
          {tab.label}
        </button>
      ))}
    </div>
  );

  const renderRecipeTab = () => {
    const start = filteredRecipes.length === 0 ? 0 : 1;
    const end = Math.min(recipeEntries === -1 ? filteredRecipes.length : recipeEntries, filteredRecipes.length);
    const displayRecipes = recipeEntries === -1 ? filteredRecipes : filteredRecipes.slice(0, recipeEntries);

    const recipeHeaders = ['Recipe Name', 'Category', 'Sub Category', 'Quantity', 'Price', 'Unit Cost'];
    const recipeRows = displayRecipes.map(r => [
      r.recipe_name || '', 
      r.category || 'General', 
      r.sub_category || '-', 
      r.quantity_display || '1', 
      formatCurrency(r.price), 
      formatCurrency(r.unit_cost)
    ]);

    return (
      <div>
        <div className="mfg-card">
          <div className="mfg-card-title">
            <i className="fas fa-scroll" style={{ color: '#4f46e5' }} /> Recipe List
          </div>

          <div className="mfg-toolbar">
            <div className="mfg-toolbar-left">
              <div className="mfg-entries-group">
                <span>Show</span>
                <select value={recipeEntries} onChange={e => setRecipeEntries(Number(e.target.value))}>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={-1}>All</option>
                </select>
                <span>entries</span>
              </div>
              <button className="mfg-btn mfg-btn-export" onClick={() => exportToCSV(recipeHeaders, recipeRows, 'recipes')} title="Export CSV"><i className="fas fa-file-csv" style={{ color: '#10b981' }} /> CSV</button>
              <button className="mfg-btn mfg-btn-export" onClick={() => exportToExcel(recipeHeaders, recipeRows, 'recipes')} title="Export Excel"><i className="fas fa-file-excel" style={{ color: '#059669' }} /> Excel</button>
              <button className="mfg-btn mfg-btn-export" onClick={() => printPage('Manufacturing Recipes', recipeHeaders, recipeRows)} title="Print"><i className="fas fa-print" style={{ color: '#64748b' }} /> Print</button>
              <button className="mfg-btn mfg-btn-export" onClick={() => exportToPDF('Manufacturing Recipes', recipeHeaders, recipeRows, 'recipes')} title="Export PDF"><i className="fas fa-file-pdf" style={{ color: '#ef4444' }} /> PDF</button>
            </div>

            <div className="mfg-toolbar-right">
              <div className="mfg-search-box">
                <i className="fas fa-search mfg-search-icon" />
                <input
                  type="text"
                  className="mfg-search"
                  placeholder="Search recipes..."
                  value={recipeSearch}
                  onChange={e => setRecipeSearch(e.target.value)}
                />
              </div>
              <button className="mfg-btn mfg-btn-primary" onClick={() => setShowRecipeModal(true)}>
                <i className="fas fa-plus" /> Add Recipe
              </button>
            </div>
          </div>

          <div className="mfg-table-wrap">
            <table className="mfg-table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}><input type="checkbox" onChange={selectAllRecipes} checked={selectedRecipes.length === recipes.length && recipes.length > 0} /></th>
                  <th>Recipe Name</th>
                  <th>Category</th>
                  <th>Sub Category</th>
                  <th>Quantity</th>
                  <th>Price <i className="fas fa-info-circle" style={{ color: '#3b82f6', marginLeft: '0.2rem' }} title="Total computed recipe price" /></th>
                  <th>Unit Cost</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {recipeLoading ? (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: '3rem' }}><i className="fas fa-circle-notch fa-spin fa-2x" style={{ color: '#4f46e5' }} /></td></tr>
                ) : displayRecipes.length === 0 ? (
                  <tr>
                    <td colSpan={8}>
                      <div className="mfg-empty-state">
                        <div className="mfg-empty-icon-box">
                          <i className="fas fa-scroll" />
                        </div>
                        <div className="mfg-empty-title">No Recipes Found</div>
                        <div className="mfg-empty-desc">
                          {recipeSearch ? 'No recipes match your search criteria. Try clearing the filter.' : 'Get started by creating your first product recipe to automate ingredient tracking and cost calculation.'}
                        </div>
                        {!recipeSearch && (
                          <button className="mfg-btn mfg-btn-primary" onClick={() => setShowRecipeModal(true)}>
                            <i className="fas fa-plus" /> Add First Recipe
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  displayRecipes.map(r => (
                    <tr key={r.id}>
                      <td><input type="checkbox" checked={selectedRecipes.includes(r.id)} onChange={() => toggleRecipeSelect(r.id)} /></td>
                      <td style={{ fontWeight: 600, color: 'var(--mfg-text-main)' }}>{r.recipe_name}</td>
                      <td><span className="mfg-badge mfg-badge-primary">{r.category || 'General'}</span></td>
                      <td>{r.sub_category || '-'}</td>
                      <td><span className="mfg-badge mfg-badge-purple">{r.quantity_display}</span></td>
                      <td style={{ fontWeight: 700, color: '#10b981' }}>{formatCurrency(r.price)}</td>
                      <td style={{ fontWeight: 600 }}>{formatCurrency(r.unit_cost)}</td>
                      <td>
                        <div className="mfg-actions" style={{ justifyContent: 'flex-end' }}>
                          <button className="mfg-action-btn view" onClick={() => handleViewRecipe(r.id)} title="View Recipe"><i className="fas fa-eye" /></button>
                          <button className="mfg-action-btn edit" onClick={() => handleEditRecipe(r.id)} title="Edit Recipe"><i className="fas fa-pen" /></button>
                          <button className="mfg-action-btn delete" onClick={() => handleDeleteRecipe(r.id)} title="Delete Recipe"><i className="fas fa-trash-alt" /></button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            <div className="mfg-mobile-cards">
              {recipeLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}><i className="fas fa-circle-notch fa-spin" /> Loading...</div>
              ) : displayRecipes.length === 0 ? (
                <div className="mfg-empty-state">
                  <div className="mfg-empty-title">No Recipes Found</div>
                </div>
              ) : (
                displayRecipes.map(r => (
                  <div key={r.id} className="mfg-mobile-card">
                    <div className="mfg-mobile-card-row">
                      <span className="mfg-mobile-card-label"><input type="checkbox" checked={selectedRecipes.includes(r.id)} onChange={() => toggleRecipeSelect(r.id)} /></span>
                      <span className="mfg-mobile-card-value" style={{ fontWeight: 700 }}>{r.recipe_name}</span>
                    </div>
                    <div className="mfg-mobile-card-row"><span className="mfg-mobile-card-label">Category</span><span className="mfg-mobile-card-value">{r.category || '-'}</span></div>
                    <div className="mfg-mobile-card-row"><span className="mfg-mobile-card-label">Quantity</span><span className="mfg-mobile-card-value">{r.quantity_display}</span></div>
                    <div className="mfg-mobile-card-row"><span className="mfg-mobile-card-label">Price</span><span className="mfg-mobile-card-value" style={{ color: '#10b981', fontWeight: 700 }}>{formatCurrency(r.price)}</span></div>
                    <div className="mfg-mobile-card-row"><span className="mfg-mobile-card-label">Unit Cost</span><span className="mfg-mobile-card-value">{formatCurrency(r.unit_cost)}</span></div>
                    <div className="mfg-actions" style={{ marginTop: '0.75rem', paddingTop: '0.5rem', borderTop: '1px solid #e2e8f0' }}>
                      <button className="mfg-btn mfg-btn-sm" onClick={() => handleViewRecipe(r.id)}><i className="fas fa-eye" /> View</button>
                      <button className="mfg-btn mfg-btn-sm" onClick={() => handleEditRecipe(r.id)}><i className="fas fa-pen" /> Edit</button>
                      <button className="mfg-btn mfg-btn-sm" onClick={() => handleDeleteRecipe(r.id)} style={{ color: '#ef4444' }}><i className="fas fa-trash" /> Delete</button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="mfg-pagination-bar">
              <span>Showing {start} to {end} of {filteredRecipes.length} entries</span>
            </div>
          </div>
        </div>

        <div className="mfg-update-bar">
          <button className="mfg-btn" onClick={handleUpdateProductPrices}>
            <i className="fas fa-sync" /> Update Product Price
          </button>
          <span className="mfg-help-text">
            <i className="fas fa-info-circle" /> Updates base product price based on computed recipe unit price
          </span>
        </div>
      </div>
    );
  };

  const renderProductionTab = () => (
    <div>
      <div className="mfg-filter-card">
        <div className="mfg-filters">
          <div className="mfg-filter-group">
            <label>Business Location</label>
            <select className="mfg-select" value={prodFilterLocation} onChange={e => setProdFilterLocation(e.target.value)}>
              <option value="">All Locations</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>

          <div className="mfg-filter-group">
            <label>Start Date</label>
            <input type="date" className="mfg-input" value={prodFilterStartDate} onChange={e => setProdFilterStartDate(e.target.value)} />
          </div>

          <div className="mfg-filter-group">
            <label>End Date</label>
            <input type="date" className="mfg-input" value={prodFilterEndDate} onChange={e => setProdFilterEndDate(e.target.value)} />
          </div>

          <div className="mfg-checkbox-group">
            <input type="checkbox" id="prod_is_final" checked={prodFilterIsFinal} onChange={e => setProdFilterIsFinal(e.target.checked)} />
            <label htmlFor="prod_is_final" style={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>Finalized Only</label>
          </div>

          <button className="mfg-btn mfg-btn-primary" onClick={() => fetchProductions(1)}>
            <i className="fas fa-filter" /> Apply Filter
          </button>
        </div>
      </div>

      <div className="mfg-card">
        <div className="mfg-card-title">
          <i className="fas fa-industry" style={{ color: '#10b981' }} /> Production Runs
        </div>

        <div className="mfg-toolbar">
          <div className="mfg-toolbar-left">
            <div className="mfg-entries-group">
              <span>Show</span>
              <select value={25} onChange={() => {}}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>entries</span>
            </div>
          </div>

          <div className="mfg-toolbar-right">
            <div className="mfg-search-box">
              <i className="fas fa-search mfg-search-icon" />
              <input
                type="text"
                className="mfg-search"
                placeholder="Search production ref/product..."
                value={prodSearch}
                onChange={e => setProdSearch(e.target.value)}
              />
            </div>
            <button className="mfg-btn mfg-btn-primary" onClick={() => setShowProductionModal(true)}>
              <i className="fas fa-plus" /> Add Production
            </button>
          </div>
        </div>

        <div className="mfg-table-wrap">
          <table className="mfg-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Ref No</th>
                <th>Location</th>
                <th>Product</th>
                <th>Quantity</th>
                <th>Total Cost</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {productionLoading ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: '3rem' }}><i className="fas fa-circle-notch fa-spin fa-2x" style={{ color: '#10b981' }} /></td></tr>
              ) : filteredProductions.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <div className="mfg-empty-state">
                      <div className="mfg-empty-icon-box" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
                        <i className="fas fa-industry" />
                      </div>
                      <div className="mfg-empty-title">No Production Batches</div>
                      <div className="mfg-empty-desc">
                        Log a new production run to update manufactured inventory balances and log raw material usages.
                      </div>
                      <button className="mfg-btn mfg-btn-primary" onClick={() => setShowProductionModal(true)}>
                        <i className="fas fa-plus" /> Add Production Run
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredProductions.map(p => (
                  <tr key={p.id}>
                    <td>{p.transaction_date ? new Date(p.transaction_date).toLocaleDateString() : '-'}</td>
                    <td style={{ fontWeight: 600, color: 'var(--mfg-primary)' }}>{p.ref_no}</td>
                    <td>{p.location_name}</td>
                    <td style={{ fontWeight: 600 }}>{p.product_name}</td>
                    <td><span className="mfg-badge mfg-badge-primary">{p.quantity}</span></td>
                    <td style={{ fontWeight: 700, color: '#10b981' }}>{formatCurrency(p.final_total)}</td>
                    <td>
                      <span className={`mfg-badge ${p.mfg_is_final ? 'mfg-badge-success' : 'mfg-badge-warning'}`}>
                        {p.mfg_is_final ? 'Finalized' : 'Draft'}
                      </span>
                    </td>
                    <td>
                      <div className="mfg-actions" style={{ justifyContent: 'flex-end' }}>
                        <button className="mfg-action-btn view" onClick={() => handleViewProduction(p.id)} title="View"><i className="fas fa-eye" /></button>
                        {p.mfg_is_final === 0 && (
                          <>
                            <button className="mfg-action-btn edit" onClick={() => handleViewProduction(p.id)} title="Edit"><i className="fas fa-pen" /></button>
                            <button className="mfg-action-btn delete" onClick={() => handleDeleteProduction(p.id)} title="Delete"><i className="fas fa-trash-alt" /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <div className="mfg-pagination-bar">
            <span>Showing {filteredProductions.length > 0 ? 1 : 0} to {filteredProductions.length} of {productions.length} entries</span>
            {prodLastPage > 1 && (
              <div className="mfg-pagination">
                <button className="mfg-btn mfg-btn-sm" disabled={prodPage <= 1} onClick={() => fetchProductions(prodPage - 1)}>Previous</button>
                <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Page {prodPage} of {prodLastPage}</span>
                <button className="mfg-btn mfg-btn-sm" disabled={prodPage >= prodLastPage} onClick={() => fetchProductions(prodPage + 1)}>Next</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderSettingsTab = () => (
    <div>
      <div className="mfg-card" style={{ maxWidth: '800px' }}>
        <div className="mfg-card-title">
          <i className="fas fa-sliders" style={{ color: '#8b5cf6' }} /> Manufacturing Module Settings
        </div>

        {settingsLoading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}><i className="fas fa-circle-notch fa-spin fa-2x" style={{ color: '#8b5cf6' }} /></div>
        ) : (
          <div className="mfg-settings-layout">
            <div className="mfg-settings-sidebar">
              <div className="mfg-settings-sidebar-btn">
                <i className="fas fa-cog" /> General Preferences
              </div>
            </div>

            <div className="mfg-settings-content">
              <div className="mfg-form-group">
                <label>Production Ref No. Prefix</label>
                <input
                  type="text"
                  className="mfg-input"
                  value={refNoPrefix}
                  onChange={e => setRefNoPrefix(e.target.value)}
                  placeholder="e.g. PRD-"
                />
              </div>

              <div className="mfg-toggle-row">
                <div className="mfg-toggle-info">
                  <div className="mfg-toggle-title">Disable Editing Ingredients Quantity</div>
                  <div className="mfg-toggle-desc">Lock ingredient quantities during production entry to ensure strict formula compliance.</div>
                </div>
                <label className="mfg-switch">
                  <input type="checkbox" checked={disableEditQty} onChange={e => setDisableEditQty(e.target.checked)} />
                  <span className="mfg-slider"></span>
                </label>
              </div>

              <div className="mfg-toggle-row">
                <div className="mfg-toggle-info">
                  <div className="mfg-toggle-title">Update Product Purchase Price</div>
                  <div className="mfg-toggle-desc">Automatically adjust product purchase prices upon finalizing a production batch.</div>
                </div>
                <label className="mfg-switch">
                  <input type="checkbox" checked={enableUpdatePrice} onChange={e => setEnableUpdatePrice(e.target.checked)} />
                  <span className="mfg-slider"></span>
                </label>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                <button className="mfg-btn mfg-btn-purple" onClick={handleSaveSettings}>
                  <i className="fas fa-save" /> Save Settings
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const dateFilterOptions = [
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'Last 7 Days', value: 'last_7_days' },
    { label: 'This Month', value: 'this_month' },
    { label: 'Last Month', value: 'last_month' },
    { label: 'This Year', value: 'this_year' },
    { label: 'Custom Range', value: 'custom_range' },
  ];

  const applyDateFilter = (preset) => {
    const today = new Date();
    const pad = n => String(n).padStart(2, '0');
    const fmt = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    let start = '', end = '';
    switch (preset) {
      case 'today': start = end = fmt(today); break;
      case 'yesterday': { const d = new Date(today); d.setDate(d.getDate() - 1); start = end = fmt(d); break; }
      case 'last_7_days': { const d = new Date(today); d.setDate(d.getDate() - 6); start = fmt(d); end = fmt(today); break; }
      case 'this_month': start = fmt(new Date(today.getFullYear(), today.getMonth(), 1)); end = fmt(today); break;
      case 'last_month': start = fmt(new Date(today.getFullYear(), today.getMonth() - 1, 1)); end = fmt(new Date(today.getFullYear(), today.getMonth(), 0)); break;
      case 'this_year': start = fmt(new Date(today.getFullYear(), 0, 1)); end = fmt(today); break;
      default: break;
    }
    setReportStartDate(start);
    setReportEndDate(end);
    setReportDateFilter(preset);
    setShowDateFilterDropdown(false);
    if (preset !== 'custom_range') fetchReport();
  };

  const renderReportTab = () => (
    <div>
      <div className="mfg-report-filters">
        <div style={{ display: 'flex', gap: '0.85rem', alignItems: 'center' }}>
          <div className="mfg-filter-group" style={{ minWidth: '220px' }}>
            <label><i className="fas fa-map-marker-alt" style={{ color: '#3b82f6', marginRight: '0.35rem' }} /> Business Location</label>
            <select className="mfg-select" value={reportLocationId} onChange={e => { setReportLocationId(e.target.value); fetchReport(); }}>
              <option value="">All Locations</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
        </div>

        <div style={{ position: 'relative' }}>
          <button className="mfg-btn mfg-btn-purple" onClick={() => setShowDateFilterDropdown(v => !v)}>
            <i className="fas fa-calendar-alt" /> Filter by Date <i className="fas fa-chevron-down" style={{ fontSize: '0.75rem' }} />
          </button>
          {showDateFilterDropdown && (
            <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 0.35rem)', background: 'var(--mfg-card-bg)', border: '1px solid var(--mfg-border)', borderRadius: '10px', boxShadow: 'var(--mfg-shadow-lg)', zIndex: 100, minWidth: '190px', overflow: 'hidden' }}>
              {dateFilterOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => applyDateFilter(opt.value)}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.6rem 0.85rem', border: 'none', background: reportDateFilter === opt.value ? 'var(--mfg-bg)' : 'var(--mfg-card-bg)', color: 'var(--mfg-text-main)', fontSize: '0.85rem', cursor: 'pointer', borderBottom: '1px solid var(--mfg-border)', fontWeight: reportDateFilter === opt.value ? 600 : 400 }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {reportDateFilter === 'custom_range' && (
        <div className="mfg-filter-card" style={{ marginBottom: '1.25rem' }}>
          <div className="mfg-filters">
            <div className="mfg-filter-group">
              <label>Start Date</label>
              <input type="date" className="mfg-input" value={reportStartDate} onChange={e => setReportStartDate(e.target.value)} />
            </div>
            <div className="mfg-filter-group">
              <label>End Date</label>
              <input type="date" className="mfg-input" value={reportEndDate} onChange={e => setReportEndDate(e.target.value)} />
            </div>
            <button className="mfg-btn mfg-btn-primary" onClick={() => fetchReport()}><i className="fas fa-filter" /> Filter</button>
          </div>
        </div>
      )}

      <div className="mfg-report-hero-grid">
        <div className="mfg-report-metric-card">
          <div className="mfg-report-metric-title"><i className="fas fa-boxes-stacked" style={{ color: '#4f46e5', marginRight: '0.4rem' }} /> Total Production</div>
          <div className="mfg-report-metric-amount">{reportLoading ? <i className="fas fa-circle-notch fa-spin" /> : formatCurrency(reportData.total_production)}</div>
        </div>
        <div className="mfg-report-metric-card cost">
          <div className="mfg-report-metric-title"><i className="fas fa-calculator" style={{ color: '#8b5cf6', marginRight: '0.4rem' }} /> Production Cost</div>
          <div className="mfg-report-metric-amount">{reportLoading ? <i className="fas fa-circle-notch fa-spin" /> : formatCurrency(reportData.total_production_cost)}</div>
        </div>
        <div className="mfg-report-metric-card sold">
          <div className="mfg-report-metric-title"><i className="fas fa-chart-pie" style={{ color: '#10b981', marginRight: '0.4rem' }} /> Total Sold</div>
          <div className="mfg-report-metric-amount">{reportLoading ? <i className="fas fa-circle-notch fa-spin" /> : formatCurrency(reportData.total_sold)}</div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          <button className="mfg-btn mfg-btn-blue" onClick={() => window.location.href = '/reports'}><i className="fas fa-box" /> Stock Report</button>
          <button className="mfg-btn" onClick={() => window.location.href = '/reports'}><i className="fas fa-barcode" /> Lot Report</button>
          <button className="mfg-btn" onClick={() => window.location.href = '/reports'}><i className="fas fa-calendar-times" /> Expiry Report</button>
          <button className="mfg-btn mfg-btn-coral" onClick={() => window.location.href = '/reports'}><i className="fas fa-list" /> Items Report</button>
        </div>
        <button className="mfg-btn mfg-btn-purple" onClick={() => window.print()}><i className="fas fa-print" /> Print Report</button>
      </div>
    </div>
  );

  const handleSaveRecipe = async () => {
    if (!recipeForm.variationId) {
      toast.warning('Please select a product variation');
      return;
    }
    const payload = {
      variation_id: recipeForm.variationId,
      total_quantity: recipeForm.totalQuantity,
      waste_percent: recipeForm.wastePercent,
      extra_cost: recipeForm.extraCost,
      production_cost_type: recipeForm.productionCostType,
      instructions: recipeForm.instructions,
      ingredients: recipeForm.ingredients
    };
    try {
      const res = await apiService.createManufacturingRecipe(payload);
      if (res && res.success) {
        toast.success(res?.msg || 'Recipe saved successfully');
        setShowAddRecipeFormModal(false);
        fetchRecipes();
      } else {
        toast.error(res?.msg || 'Failed to save recipe');
      }
    } catch (e) {
      toast.error('Error saving recipe: ' + e.message);
    }
  };

  const renderRecipeModal = () => {
    if (!showRecipeModal) return null;
    return (
      <div className="mfg-modal-overlay" onClick={() => setShowRecipeModal(false)}>
        <div className="mfg-modal" onClick={e => e.stopPropagation()}>
          <div className="mfg-modal-header">
            <h3>Choose Target Product</h3>
            <button className="mfg-modal-close" onClick={() => setShowRecipeModal(false)}><i className="fas fa-times" /></button>
          </div>
          <div className="mfg-modal-body">
            <div className="mfg-form-group">
              <label>Select Manufactured Product:</label>
              <select className="mfg-select" value={recipeProductId} onChange={e => setRecipeProductId(e.target.value)}>
                <option value="">Please Select Product</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="mfg-form-group">
              <label>Copy Template From Existing Recipe:</label>
              <select className="mfg-select" value={copyRecipeId} onChange={e => setCopyRecipeId(e.target.value)}>
                <option value="">None (Build From Scratch)</option>
                {recipes.map(r => <option key={r.id} value={r.id}>{r.recipe_name}</option>)}
              </select>
            </div>
          </div>
          <div className="mfg-modal-footer" style={{ justifyContent: 'center' }}>
            <button className="mfg-btn" onClick={() => setShowRecipeModal(false)}><i className="fas fa-times" /> Cancel</button>
            <button className="mfg-btn mfg-btn-primary" onClick={() => {
              if (!recipeProductId) { toast.warning('Please select a product'); return; }
              setRecipeForm(prev => ({ ...prev, variationId: recipeProductId }));
              setShowRecipeModal(false);
              setShowAddRecipeFormModal(true);
            }}><i className="fas fa-arrow-right" /> Continue</button>
          </div>
        </div>
      </div>
    );
  };

  const renderAddRecipeFormModal = () => {
    if (!showAddRecipeFormModal) return null;
    const addIng = () => setRecipeForm(prev => ({ ...prev, ingredients: [...prev.ingredients, { ingredient_id: '', quantity: 1, waste_percent: 0 }] }));
    const removeIng = (idx) => setRecipeForm(prev => ({ ...prev, ingredients: prev.ingredients.filter((_, i) => i !== idx) }));
    const updateIng = (idx, field, val) => {
      setRecipeForm(prev => {
        const copy = [...prev.ingredients];
        copy[idx] = { ...copy[idx], [field]: val };
        return { ...prev, ingredients: copy };
      });
    };

    return (
      <div className="mfg-modal-overlay" onClick={() => setShowAddRecipeFormModal(false)}>
        <div className="mfg-modal" style={{ maxWidth: '680px' }} onClick={e => e.stopPropagation()}>
          <div className="mfg-modal-header">
            <h3>Configure Recipe Ingredients</h3>
            <button className="mfg-modal-close" onClick={() => setShowAddRecipeFormModal(false)}><i className="fas fa-times" /></button>
          </div>
          <div className="mfg-modal-body">
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.25rem' }}>
              <div className="mfg-form-group" style={{ flex: 1 }}>
                <label>Total Yield Quantity:</label>
                <input type="number" className="mfg-input" value={recipeForm.totalQuantity} onChange={e => setRecipeForm({ ...recipeForm, totalQuantity: Number(e.target.value) })} />
              </div>
              <div className="mfg-form-group" style={{ flex: 1 }}>
                <label>Estimated Waste %:</label>
                <input type="number" className="mfg-input" value={recipeForm.wastePercent} onChange={e => setRecipeForm({ ...recipeForm, wastePercent: Number(e.target.value) })} />
              </div>
            </div>

            <h4 style={{ fontSize: '0.95rem', fontWeight: 700, marginBottom: '0.75rem', color: '#0f172a' }}>Recipe Formula Items</h4>
            {recipeForm.ingredients.map((ing, idx) => (
              <div key={idx} style={{ display: 'flex', gap: '0.6rem', marginBottom: '0.65rem', alignItems: 'center' }}>
                <select className="mfg-select" style={{ flex: 2 }} value={ing.ingredient_id} onChange={e => updateIng(idx, 'ingredient_id', e.target.value)}>
                  <option value="">Select Raw Ingredient Product</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <input type="number" className="mfg-input" style={{ flex: 1 }} placeholder="Qty" value={ing.quantity} onChange={e => updateIng(idx, 'quantity', Number(e.target.value))} />
                <input type="number" className="mfg-input" style={{ flex: 1 }} placeholder="Waste %" value={ing.waste_percent} onChange={e => updateIng(idx, 'waste_percent', Number(e.target.value))} />
                {recipeForm.ingredients.length > 1 && (
                  <button className="mfg-action-btn delete" onClick={() => removeIng(idx)} title="Remove Item"><i className="fas fa-trash-alt" /></button>
                )}
              </div>
            ))}
            <button className="mfg-btn mfg-btn-sm" onClick={addIng} style={{ marginTop: '0.5rem' }}><i className="fas fa-plus" /> Add Ingredient Row</button>
          </div>
          <div className="mfg-modal-footer" style={{ justifyContent: 'center' }}>
            <button className="mfg-btn" onClick={() => setShowAddRecipeFormModal(false)}><i className="fas fa-times" /> Cancel</button>
            <button className="mfg-btn mfg-btn-primary" onClick={handleSaveRecipe}><i className="fas fa-save" /> Save Recipe</button>
          </div>
        </div>
      </div>
    );
  };

  const renderViewRecipeModal = () => {
    if (!viewRecipeModal) return null;
    const r = viewRecipeModal.recipe || {};
    const ingredients = viewRecipeModal.ingredients || [];

    return (
      <div className="mfg-modal-overlay" onClick={() => setViewRecipeModal(null)}>
        <div className="mfg-modal" style={{ maxWidth: '580px' }} onClick={e => e.stopPropagation()}>
          <div className="mfg-modal-header">
            <h3>Recipe Details: {r.recipe_name || r.name}</h3>
            <button className="mfg-modal-close" onClick={() => setViewRecipeModal(null)}><i className="fas fa-times" /></button>
          </div>
          <div className="mfg-modal-body" style={{ lineHeight: 1.6 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem', marginBottom: '1.25rem', background: 'var(--mfg-bg)', padding: '1rem', borderRadius: '10px' }}>
              <div><strong>Category:</strong> <span className="mfg-badge mfg-badge-primary">{r.category || 'General'}</span></div>
              <div><strong>Total Yield:</strong> {r.quantity_display || r.total_quantity || '-'}</div>
              <div><strong>Total Price:</strong> <span style={{ color: 'var(--mfg-success)', fontWeight: 700 }}>{formatCurrency(r.price || r.final_price)}</span></div>
              <div><strong>Unit Cost:</strong> {formatCurrency(r.unit_cost)}</div>
            </div>

            {ingredients.length > 0 && (
              <>
                <h4 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '0.5rem' }}>Ingredients List:</h4>
                <div className="mfg-table-wrap">
                  <table className="mfg-table" style={{ fontSize: '0.82rem' }}>
                    <thead>
                      <tr><th>Ingredient</th><th>Quantity</th></tr>
                    </thead>
                    <tbody>
                      {ingredients.map((ing, i) => (
                        <tr key={i}>
                          <td style={{ fontWeight: 600 }}>{ing.full_name || ing.variation?.full_name || 'Ingredient'}</td>
                          <td>{ing.quantity} {ing.unit}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
          <div className="mfg-modal-footer" style={{ justifyContent: 'center' }}>
            <button className="mfg-btn mfg-btn-primary" onClick={() => setViewRecipeModal(null)}>Close</button>
          </div>
        </div>
      </div>
    );
  };

  const renderViewProductionModal = () => {
    if (!viewProductionModal) return null;
    const p = viewProductionModal.production_purchase || {};

    return (
      <div className="mfg-modal-overlay" onClick={() => setViewProductionModal(null)}>
        <div className="mfg-modal" style={{ maxWidth: '580px' }} onClick={e => e.stopPropagation()}>
          <div className="mfg-modal-header">
            <h3>Production Details: {p.ref_no}</h3>
            <button className="mfg-modal-close" onClick={() => setViewProductionModal(null)}><i className="fas fa-times" /></button>
          </div>
          <div className="mfg-modal-body" style={{ lineHeight: 1.6 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem', background: 'var(--mfg-bg)', padding: '1rem', borderRadius: '10px' }}>
              <div><strong>Ref No:</strong> <span style={{ color: 'var(--mfg-primary)', fontWeight: 600 }}>{p.ref_no}</span></div>
              <div><strong>Location:</strong> {p.location_name}</div>
              <div><strong>Product:</strong> {p.product_name}</div>
              <div><strong>Quantity:</strong> {p.quantity}</div>
              <div><strong>Total Cost:</strong> <span style={{ color: '#10b981', fontWeight: 700 }}>{formatCurrency(p.final_total)}</span></div>
              <div><strong>Status:</strong> <span className={`mfg-badge ${p.mfg_is_final ? 'mfg-badge-success' : 'mfg-badge-warning'}`}>{p.mfg_is_final ? 'Finalized' : 'Draft'}</span></div>
            </div>
          </div>
          <div className="mfg-modal-footer" style={{ justifyContent: 'center' }}>
            <button className="mfg-btn mfg-btn-primary" onClick={() => setViewProductionModal(null)}>Close</button>
          </div>
        </div>
      </div>
    );
  };

  const handleCreateProduction = async () => {
    if (!prodForm.locationId || !prodForm.productId) {
      toast.warning('Please select Business Location and Product');
      return;
    }
    const payload = {
      location_id: prodForm.locationId,
      variation_id: prodForm.productId,
      quantity: prodForm.quantity,
      ref_no: prodForm.refNo,
      transaction_date: prodForm.manufacturingDate,
      mfg_wasted_units: prodForm.wastedQty,
      production_cost: prodForm.productionCost,
      mfg_production_cost_type: prodForm.productionCostType,
      finalize: prodForm.finalize ? 1 : 0
    };
    try {
      const res = await apiService.createManufacturingProduction(payload);
      if (res && res.success) {
        toast.success(res?.msg || 'Production created successfully');
        setShowProductionModal(false);
        const selProduct = products.find(p => p.id == prodForm.productId);
        const selLocation = locations.find(l => l.id == prodForm.locationId);
        const newProd = {
          id: res?.id || Math.random(),
          transaction_date: prodForm.manufacturingDate,
          ref_no: prodForm.refNo || 'PRD-NEW',
          location_name: selLocation?.name || 'Main Location',
          product_name: selProduct?.name || 'Product',
          quantity: prodForm.quantity,
          final_total: prodForm.productionCost || 0,
          mfg_is_final: prodForm.finalize ? 1 : 0
        };
        setProductions(prev => [newProd, ...prev]);
      } else {
        toast.error(res?.msg || 'Failed to create production');
      }
    } catch (e) {
      toast.error('Error creating production: ' + e.message);
    }
  };

  const renderProductionModal = () => {
    if (!showProductionModal) return null;
    const handleProdChange = (field, value) => setProdForm(prev => ({ ...prev, [field]: value }));

    return (
      <div className="mfg-modal-overlay" onClick={() => setShowProductionModal(false)}>
        <div className="mfg-modal" style={{ maxWidth: '750px', width: '100%' }} onClick={e => e.stopPropagation()}>
          <div className="mfg-modal-header">
            <h3>New Production Run</h3>
            <button className="mfg-modal-close" onClick={() => setShowProductionModal(false)}><i className="fas fa-times" /></button>
          </div>
          <div className="mfg-modal-body">
            <div className="mfg-filter-card" style={{ marginBottom: '1.25rem' }}>
              <div className="mfg-form-grid">
                <div className="mfg-form-group">
                  <label>Reference No:</label>
                  <input type="text" className="mfg-input" value={prodForm.refNo} onChange={e => handleProdChange('refNo', e.target.value)} placeholder="Auto-generated if left blank" />
                </div>
                <div className="mfg-form-group">
                  <label>Manufacturing Date <span style={{ color: '#ef4444' }}>*</span></label>
                  <input type="datetime-local" className="mfg-input" value={prodForm.manufacturingDate} onChange={e => handleProdChange('manufacturingDate', e.target.value)} />
                </div>
                <div className="mfg-form-group">
                  <label>Business Location <span style={{ color: '#ef4444' }}>*</span></label>
                  <select className="mfg-select" value={prodForm.locationId} onChange={e => handleProdChange('locationId', e.target.value)}>
                    <option value="">Please Select</option>
                    {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <div className="mfg-form-group">
                  <label>Target Product <span style={{ color: '#ef4444' }}>*</span></label>
                  <select className="mfg-select" value={prodForm.productId} onChange={e => handleProdChange('productId', e.target.value)}>
                    <option value="">Please Select</option>
                    {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div className="mfg-form-group">
                  <label>Production Quantity <span style={{ color: '#ef4444' }}>*</span></label>
                  <input type="number" className="mfg-input" value={prodForm.quantity} onChange={e => handleProdChange('quantity', Number(e.target.value))} />
                </div>
                <div className="mfg-form-group">
                  <label>Wasted Quantity:</label>
                  <input type="number" className="mfg-input" value={prodForm.wastedQty} onChange={e => handleProdChange('wastedQty', Number(e.target.value))} />
                </div>
              </div>
            </div>

            <div className="mfg-filter-card">
              <h4 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '0.75rem' }}>Production Cost Calculation</h4>
              <div className="mfg-form-grid">
                <div className="mfg-form-group">
                  <label>Extra Cost Amount:</label>
                  <input type="number" className="mfg-input" value={prodForm.productionCost} onChange={e => handleProdChange('productionCost', Number(e.target.value))} />
                </div>
                <div className="mfg-form-group">
                  <label>Cost Type:</label>
                  <select className="mfg-select" value={prodForm.productionCostType} onChange={e => handleProdChange('productionCostType', e.target.value)}>
                    <option value="fixed">Fixed Amount</option>
                    <option value="percentage">Percentage %</option>
                    <option value="per_unit">Per Unit</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="mfg-modal-footer" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="mfg-checkbox-group">
              <input type="checkbox" id="finalize_prod" checked={prodForm.finalize} onChange={e => handleProdChange('finalize', e.target.checked)} />
              <label htmlFor="finalize_prod" style={{ fontWeight: 600, fontSize: '0.88rem' }}>Finalize Production Batch</label>
            </div>

            <div style={{ display: 'flex', gap: '0.65rem' }}>
              <button className="mfg-btn" onClick={() => setShowProductionModal(false)}><i className="fas fa-times" /> Cancel</button>
              <button className="mfg-btn mfg-btn-primary" onClick={handleCreateProduction}><i className="fas fa-check" /> Submit Production</button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mfg-page">
      <div className="mfg-page-header">
        <div className="mfg-page-header-title-wrap">
          <div className="mfg-page-icon">
            <i className="fas fa-industry" />
          </div>
          <div>
            <h1>Manufacturing Hub</h1>
            <div className="mfg-page-header-subtitle">Manage raw ingredients, recipe definitions, and production execution</div>
          </div>
        </div>
      </div>

      {renderStatsHeader()}
      {renderTabsNav()}

      {activeTab === 'recipe' && renderRecipeTab()}
      {activeTab === 'production' && renderProductionTab()}
      {activeTab === 'settings' && renderSettingsTab()}
      {activeTab === 'report' && renderReportTab()}

      {renderRecipeModal()}
      {renderAddRecipeFormModal()}
      {renderViewRecipeModal()}
      {renderViewProductionModal()}
      {renderProductionModal()}
    </div>
  );
};

export default Manufacturing;
